import React, { useCallback } from 'react';
import { Card, CardContent, FormControl, MenuItem, IconButton, InputLabel } from '@material-ui/core';
import Select from '@material-ui/core/Select';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import PropTypes from 'prop-types';
import { fieldShape } from './Refiner.props';
import { makeStyles } from 'utils';
import classNames from './Refiner.module.css';
import RefinerSelectField from './RefinerSelectField';
import RefinerTextField from './RefinerTextField';
import RefinerDateField from './RefinerDateField';
import RefinerNumberSelectField from './RefinerNumberSelectField';

const styles = makeStyles(classNames);

const RefinerRow = ({
    fields,
    isReadOnly,
    selectedField = { name: '' },
    selector,
    uniqueFieldValues,
    value,
    onFieldChange,
    onOptionChange,
    onRemoveClick
}) => {
    const isFieldSelected = !!(selectedField && selectedField.name && selectedField.name !== '');
    const selectorName = isFieldSelected ? selectedField.name : 'new';
    const idBase = `refinement-row_${selectorName}`;

    let menuItems;
    if (isFieldSelected) {
        menuItems = (
            <MenuItem key={selectedField.name} value={selectedField.name}>
                {selectedField.label}
            </MenuItem>
        );
    } else {
        menuItems = [
            <MenuItem key="-1" value="" disabled>Refine by</MenuItem>
        ];
        menuItems = menuItems.concat(fields.map(field => (
            <MenuItem key={field.name} value={field.name}>
                {field.label}
            </MenuItem>
        )));
    }

    const handleFieldChange = useCallback(e => {
        const newValue = e.target.value;
        const newField = fields.find(f => f.name === newValue);
        if (newField && typeof (onFieldChange) === 'function') {
            onFieldChange(selector, newField);
        }
    }, [onFieldChange, fields, selector]);

    const handleOptionChange = useCallback(e => {
        if (e && e.target) {
            const newValue = e.target.value;
            if (typeof (onOptionChange) === 'function') {
                onOptionChange(newValue, selector, selectedField);
            }
        }
    }, [onOptionChange, selector, selectedField]);

    const handleRemoveClick = useCallback(e => {
        if (typeof(onRemoveClick) === 'function') {
            onRemoveClick(selector);
        }
    }, [onRemoveClick, selector]);

    const refinerFieldProps = {
        idBase,
        readOnly: isReadOnly,
        selectedField,
        selector,
        uniqueFieldValues,
        value,
        onChange: handleOptionChange
    };

    return (
        <div id={idBase} data-testid="refinement-row" className={styles('row')}>
            <Card variant="outlined">
                <CardContent>
                    <FormControl className={styles('row__form-control')}>
                        <InputLabel id={idBase + '_field-label'} htmlFor={idBase + '_field'} shrink>Field</InputLabel>
                        <Select
                            id={idBase + '_field'}
                            className={styles('row__input')}
                            value={isFieldSelected ? selectedField.name : ''}
                            readOnly={isFieldSelected}
                            displayEmpty
                            fullWidth
                            inputProps={{ 'aria-label': 'Field' }}
                            onChange={handleFieldChange}
                        >
                            {menuItems}
                        </Select>
                    </FormControl>
                    {isFieldSelected && selectedField.type === 'text' && (
                        <RefinerTextField {...refinerFieldProps} />
                    )}
                    {isFieldSelected && (selectedField.type === 'list' || selectedField.type === 'select') && (
                        <RefinerSelectField {...refinerFieldProps} />
                    )}
                    {isFieldSelected && (selectedField.type === 'date') && (
                        <RefinerDateField {...refinerFieldProps} />
                    )}
                    {isFieldSelected && selectedField.type === 'number-select' && (
                        <RefinerNumberSelectField {...refinerFieldProps} />
                    )}
                    {!isReadOnly && (
                        <IconButton onClick={handleRemoveClick} color="secondary" aria-label="Remove">
                            <RemoveCircleIcon />
                        </IconButton>
                    )}
                </CardContent>
            </Card>
        </div>
    );

}

RefinerRow.propTypes = {
    fields: PropTypes.arrayOf(PropTypes.shape(fieldShape)).isRequired,
    isReadOnly: PropTypes.bool,
    selectedField: PropTypes.shape(fieldShape),
    selector: PropTypes.any,
    uniqueFieldValues: PropTypes.arrayOf(PropTypes.string),
    value: PropTypes.any,
    onFieldChange: PropTypes.func,
    onOptionChange: PropTypes.func,
    onRemoveClick: PropTypes.func
};


export default RefinerRow;